import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';

export interface UserSubscriptions {
  hasCourse: boolean;
  hasSalepage: boolean;
  hasAny: boolean;
}

/**
 * Check if user has active course subscription
 */
export async function hasActiveCourseSubscription(userId: string): Promise<boolean> {
  try {
    const subscriptionsRef = collection(db, 'subscriptions');
    const q = query(
      subscriptionsRef,
      where('userId', '==', userId),
      where('status', 'in', ['active', 'grace_period'])
    );
    const snapshot = await getDocs(q);
    
    for (const doc of snapshot.docs) {
      const data = doc.data();
      if (data.planId && !data.planId.includes('salepage')) {
        return true;
      }
    }
    return false;
  } catch (error) {
    console.error('Error checking course subscription:', error);
    return false;
  }
}

/**
 * Check if user has active salepage subscription
 */
export async function hasActiveSalepageSubscription(userId: string): Promise<boolean> {
  try {
    const subscriptionsRef = collection(db, 'subscriptions');
    const q = query(
      subscriptionsRef,
      where('userId', '==', userId),
      where('status', 'in', ['active', 'grace_period'])
    );
    const snapshot = await getDocs(q);
    
    for (const doc of snapshot.docs) {
      const data = doc.data();
      if (data.planId && data.planId.includes('salepage')) {
        return true;
      }
    }
    return false;
  } catch (error) {
    console.error('Error checking salepage subscription:', error);
    return false;
  }
}

/**
 * Get all user's active subscriptions
 */
export async function getUserSubscriptions(userId: string): Promise<UserSubscriptions> {
  try {
    const [hasCourse, hasSalepage] = await Promise.all([
      hasActiveCourseSubscription(userId),
      hasActiveSalepageSubscription(userId)
    ]);

    return {
      hasCourse,
      hasSalepage,
      hasAny: hasCourse || hasSalepage
    };
  } catch (error) {
    console.error('Error getting user subscriptions:', error);
    return {
      hasCourse: false,
      hasSalepage: false,
      hasAny: false
    };
  }
}

/**
 * Get default redirect path based on user subscriptions
 */
export function getDefaultRedirectPath(subscriptions: UserSubscriptions, isAdmin: boolean = false): string {
  if (isAdmin) {
    return '/admin';
  }

  if (subscriptions.hasCourse) {
    return '/instructor/dashboard';
  }

  if (subscriptions.hasSalepage) {
    return '/salepage/dashboard';
  }

  return '/pricing';
}
